/**
 * Piece Palette Component
 *
 * Displays all available pieces that the player can select and place on the grid.
 * Features:
 * - Shows all 5 piece types (T, S, O, L, bonus)
 * - Highlights currently selected piece
 * - Grayed out for placed pieces
 * - Disabled when placement is locked
 */

import { getPieceDefinition } from '../utils/pieceDefinitions';
import { COPY } from '../copy';

export default function PiecePalette({
  availablePieces = [],
  selectedPiece = null,
  onSelectPiece,
  isLocked = false,
}) {
  return (
    <div
      data-testid="piece-palette"
      className={`flex gap-4 p-4 bg-dark-purple border-2 border-neon-cyan/50 rounded-lg ${
        isLocked ? 'opacity-50' : 'opacity-100'
      }`}
      aria-disabled={isLocked}
    >
      {availablePieces.map((piece) => {
        const isSelected = selectedPiece?.type === piece.type;
        const borderColor = isSelected
          ? 'border-neon-pink shadow-glow-pink'
          : 'border-neon-cyan/30';

        return (
          <button
            key={piece.type}
            data-testid={`palette-piece-${piece.type}`}
            onClick={() => !isLocked && onSelectPiece(piece.type)}
            disabled={isLocked}
            className={`p-3 border-4 ${borderColor} bg-dark-charcoal rounded-lg transition-all hover:border-neon-cyan hover:shadow-glow-cyan disabled:cursor-not-allowed disabled:hover:border-neon-cyan/30 disabled:hover:shadow-none`}
            aria-label={COPY.accessibility.pieceButton(piece.name)}
          >
            <div className="relative w-20 h-20 flex items-center justify-center">
              {/* Render piece preview */}
              {(() => {
                // Calculate bounds of the piece to center it properly
                const minX = Math.min(...piece.shape.map((c) => c.x));
                const maxX = Math.max(...piece.shape.map((c) => c.x));
                const minY = Math.min(...piece.shape.map((c) => c.y));
                const maxY = Math.max(...piece.shape.map((c) => c.y));

                const cellSize = 18;
                const containerSize = 80; // w-20 = 80px

                // Calculate piece dimensions
                const pieceWidth = (maxX - minX + 1) * cellSize;
                const pieceHeight = (maxY - minY + 1) * cellSize;

                // Calculate offset to center the piece
                const offsetX =
                  (containerSize - pieceWidth) / 2 - minX * cellSize;
                const offsetY =
                  (containerSize - pieceHeight) / 2 - minY * cellSize;

                return piece.shape.map((coord, index) => (
                  <div
                    key={index}
                    className="absolute"
                    style={{
                      width: `${cellSize}px`,
                      height: `${cellSize}px`,
                      backgroundColor: piece.color,
                      left: `${coord.x * cellSize + offsetX}px`,
                      top: `${coord.y * cellSize + offsetY}px`,
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                    }}
                  />
                ));
              })()}
            </div>
            <p className="text-xs text-center mt-2 font-semibold text-gray-300">
              {piece.name}
            </p>
          </button>
        );
      })}
    </div>
  );
}
